/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2022
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import ApiCommon from './api-common';
import { get } from 'lodash';

export default class OpenApiV2 extends ApiCommon {
  /**
   * checks that the given file is a supported OpenAPI version
   */
  isSupported(openApi) {
    if (!openApi || typeof openApi !== 'object') {
      return false;
    }
    if (openApi.swagger) {
      return true;
    }

    return false;
  }

  /**
   * get endpoint
   */
  getEndpoints(openApi, options = {}) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }

    let endpoints = [];

    if (openApi.endpoint) {
      endpoints = endpoints.concat({ url: openApi.endpoint });
    } else if (openApi['x-ibm-configuration'] && openApi['x-ibm-configuration']['servers'] && openApi['x-ibm-configuration']['servers'].length > 0) {
      for (let i = 0; i < openApi['x-ibm-configuration']['servers'].length; i++) {
        endpoints = endpoints.concat({
          url: openApi['x-ibm-configuration']['servers'][i].url,
          types: this.getEndpointTypes(openApi['x-ibm-configuration']['servers'][i].type)
        });
      }
    } else if (
      openApi['x-ibm-configuration'] &&
      openApi['x-ibm-configuration']['graphql-server-config'] &&
      openApi['x-ibm-configuration']['graphql-server-config']['server-url']
    ) {
      endpoints = endpoints.concat({ url: openApi['x-ibm-configuration']['graphql-server-config']['server-url'], types: null });
    } else if (openApi['x-ibm-endpoints'] && openApi['x-ibm-endpoints'].length > 0) {
      for (let i = 0; i < openApi['x-ibm-endpoints'].length; i++) {
        let xibm_endpoint = openApi['x-ibm-endpoints'][i].endpointUrl;
        if (openApi.basePath) {
          xibm_endpoint = xibm_endpoint + openApi.basePath;
        }
        endpoints = endpoints.concat({ url: xibm_endpoint, types: this.getEndpointTypes(openApi['x-ibm-endpoints'][i].type) });
      }
    } else if (openApi.swagger) {
      let host = '';

      if (openApi.host) {
        host = openApi.host;
      } else {
        host = window.location.host;
      }
      var baseEndpoint = '://' + host;
      if (openApi.schemes) {
        for (let i = 0; i < openApi.schemes.length; i++) {
          let endpoint = openApi.schemes[i] + baseEndpoint;
          if (openApi.basePath) {
            endpoint = endpoint + openApi.basePath;
          }
          endpoints = endpoints.concat({ url: endpoint, types: null });
        }
      } else {
        let endpoint = 'https' + baseEndpoint;
        if (openApi.basePath) {
          endpoint = endpoint + openApi.basePath;
        }
        endpoints = endpoints.concat({ url: endpoint, types: null });
      }
    }
    // validate the endpoints are actually URLs not just URIs -- if they are
    // not URIs then add window host.
    for (var key in endpoints) {
      if (Object.prototype.hasOwnProperty.call(endpoints, key)) {
        endpoints[key].url = this.resolveRelativeUrl(endpoints[key].url);
      }
    }
    return endpoints;
  }

  /**
   * returns the body parameter, if applicable
   */
  getBodyParameter(operation, path, contentType, openApi) {
    if (!operation || !openApi || !this.isSupported(openApi)) {
      return;
    }

    if (openApi.swagger) {
      let parameters = this.getParameters(operation, path, contentType, openApi);
      let bodyParams = Object.keys(parameters).filter(function (parameterName) {
        return parameters[parameterName].in === 'body';
      });

      if (bodyParams.length > 0) {
        return parameters[bodyParams[0]];
      }
    }
  }

  /**
   * returns all schema definitions
   */
  getSchemas(openApi) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }

    return openApi.definitions;
  }

  /**
   * returns all schema definitions with their correct place in the object
   */
  getSchemasFullPath(openApi) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }

    return { definitions: openApi.definitions };
  }

  /**
   * Get the root of the definitions or schemas from the Object
   * returned by getSchemasFullPath();
   */
  getSchemasRoot(schemasFullPath) {
    let rootDefinitions = null;

    if (schemasFullPath.definitions) {
      rootDefinitions = schemasFullPath.definitions;
    }

    return rootDefinitions;
  }

  /**
   * returns all security definitions
   */
  getSecurityDefinitions(endpoint) {
    let securityDefinitions = this.api.securityDefinitions;
    if (!securityDefinitions) return;

    let oauthServers = get(this.api, 'x-ibm-configuration.oauth-servers');
    if (oauthServers) {
      securityDefinitions = JSON.parse(JSON.stringify(securityDefinitions));
      Object.entries(oauthServers).forEach(([definitionName, oauthUrls]) => {
        if (securityDefinitions[definitionName]) {
          securityDefinitions[definitionName].tokenUrl = oauthUrls.tokenUrls;
          securityDefinitions[definitionName].authorizationUrl = oauthUrls.authorizationUrls;
          securityDefinitions[definitionName].refreshUrl = oauthUrls.refreshUrls;
        }
      });
    }

    // if there's an endpoint, we need to resolve the security definition urls
    if (endpoint) {
      securityDefinitions = this.resolveOauthUrls(securityDefinitions, endpoint);
    }

    return securityDefinitions;
  }

  /**
   * returns the supported content types for the operation
   */
  getContentTypes(operation, openApi) {
    if (!operation || !openApi || !this.isSupported(openApi)) {
      return [];
    }

    let contentTypes = [];
    if (openApi.swagger) {
      if (operation.consumes && operation.consumes.length > 0) {
        contentTypes = operation.consumes;
      } else if (openApi.consumes) {
        contentTypes = openApi.consumes;
      }
    }
    contentTypes = JSON.parse(JSON.stringify(contentTypes));

    // if the API is enforced and has a body parameter assume default content types
    if (contentTypes.length === 0 && openApi['x-ibm-configuration']) {
      // If there are no parameters, give up and return whatever we have so far
      if (!operation.parameters) {
        return contentTypes;
      }

      operation.parameters.forEach(function (parameter) {
        if (parameter.in === 'formData') {
          contentTypes.push('application/x-www-form-urlencoded');
        }
        if (parameter.in === 'body') {
          if (openApi['x-ibm-configuration']['enforced']) {
            contentTypes.push('application/json');
            contentTypes.push('application/xml');
          } else {
            contentTypes.push('application/json');
          }
        }
      });
    }
    return contentTypes;
  }

  /**
   * Populates the config option for the given security flow definition
   */
  getSecurityFlowConfig(flow, definitions, credentials, endpoint) {
    if (!flow) {
      return;
    }

    let config = this.getSecurityFlowConfigDefaults();

    Object.keys(flow).forEach((securityDef) => {
      if (!this.securityDefinitionFound(definitions, securityDef)) {
        return;
      }

      let thisDef = definitions[securityDef];
      let thisFlow = flow[securityDef];

      config = this.setApikeySecurityTypeConfig(config, thisDef);
      config = this.setBasicSecurityTypeConfig(config, thisDef);
      config = this.setOauthSecurityTypeConfig(config, thisDef, thisFlow, endpoint);
    });

    config = this.setRemainingConfig(config, credentials);

    return config;
  }

  /**
   * Sets the config for the basic security type definition
   */
  setBasicSecurityTypeConfig(config, securityDef) {
    if (securityDef.type === 'basic') {
      config.requiresBasicAuth = true;
    }

    return config;
  }

  /**
   * Sets the config for the oauth security type definition
   */
  setOauthSecurityTypeConfig(config, securityDef, flowScopes, endpoint) {
    if (securityDef.type === 'oauth2') {
      config.requiresOauth = true;
      config.requiresClientId = true;
      config.oauthFlow = securityDef.flow;
      if (securityDef.flow === 'application' || securityDef.flow === 'accessCode' || securityDef.flow === 'password') {
        config.requiresClientSecret = true;
      }

      if (securityDef.flow === 'implicit' || securityDef.flow === 'accessCode') {
        config.requiresRedirectUri = true;
      }

      let urls = this.getFullOauthUrls(securityDef, endpoint);
      if (securityDef.authorizationUrl) {
        config.oauthAuthUrl = urls.authorizationUrl;
        config.requiresAuthorizationSection = true;
      }

      if (securityDef.tokenUrl) {
        config.oauthTokenUrl = urls.tokenUrl;
        config.requiresAuthorizationSection = true;
      }

      if (securityDef.scopes && Object.keys(securityDef.scopes).length > 0 && flowScopes.length > 0) {
        config.oauthScopes = [];
        // remove any x- extensions
        for (let scope in securityDef.scopes) {
          if (scope.indexOf('x-') !== 0 && flowScopes.indexOf(scope) > -1) {
            config.oauthScopes.push(scope);
          }
        }
      }
    }

    return config;
  }

  /**
   * returns the supported accept types for the operation
   */
  getAccepts(operation, openApi) {
    if (!operation || !openApi || !this.isSupported(openApi)) {
      return [];
    }

    let accepts = [];
    if (openApi.swagger) {
      if (operation.produces && operation.produces.length > 0) {
        accepts = operation.produces;
      } else if (openApi.produces) {
        accepts = openApi.produces;
      }
    }
    accepts = JSON.parse(JSON.stringify(accepts));

    if (accepts.length === 0) {
      // if the API is enforced assume default content types
      if (openApi['x-ibm-configuration'] && openApi['x-ibm-configuration']['enforced']) {
        accepts.push('application/json');
        accepts.push('application/xml');
      } else {
        accepts.push('application/json');
      }
    }
    return accepts;
  }
}
